/* Next */
import { useRouter } from 'next/router'

/* React */
import { useEffect, useState } from 'react'

/* Redux */
import { useSelector, useDispatch } from 'src/redux/hooks'
import { shallowEqual } from 'react-redux'

/* Types */
import { isBrandsResponse } from 'src/shared/frontendTypes/brand'
import { filterProducts, filterProductsResponse, isFilterProductsResponse, isFilterMetadataResponse, isSearchPreviewResponse } from 'src/shared/frontendTypes/productsFilter'
import { tProductMagazineReference, tProductPreview } from 'src/shared/frontendTypes/product'
import { MenuPageBuilder, isMenuPageBuilderActive } from 'src/shared/dbTypes/menuPageBuilder'
import type { BreadcrumbItem } from 'src/shared/frontendTypes/product'
import type { item as NavigationItem, tMenu } from 'src/shared/frontendTypes/navigation'

/* Functions */
import { joinSearchRequest, peekSearchRequest, requestSearch, searchPreviewRequest } from 'src/functions/searchRequests'
import { requestCacheKey } from 'src/functions/requestCache'
import { listingPresentation } from 'src/functions/listingPresentation'
import { listingPagePath, listingPageTitle } from 'src/functions/listingSeo'
import { resetCompletedSearch, trackCompletedSearch } from 'src/functions/commerceTracking'
import { buildNavigationBreadcrumb } from 'src/functions/navigationBreadcrumbs'
import type { LandingPageImageAiGenerated } from 'src/functions/landingPageImageAiGenerated'

/* Components */
import Filter from 'src/pages/Filter/Filter'
import LandingPage from 'src/pageBuilder/LandingPage'
import useNavigationPaths from 'src/layout/navigationPaths'

/* Options */
import config from 'src/config'

/* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */

/* Erweiterte Props-Schnittstelle um die SEO-Felder und canonicalUrl */
interface Props {
  type?: 'menu' | 'brand' | 'brandMenu'
  mid?: number
  bid?: number
  title?: string
  description?: string
  products?: tProductPreview[]
  count?: number
  productsGeneratedAt?: number
  initialPage?: number
  filterMetadata?: Pick<filterProducts, 'filterTree' | 'filterBrands'>
  metaTitle?: string
  metaDescription?: string
  metaKeywords?: string
  canonicalUrl?: string
  brandLogo?: string
  stock?: boolean
  sale?: boolean
  pageBuilder?: MenuPageBuilder
  sitePageBuilder?: MenuPageBuilder
  breadcrumb?: BreadcrumbItem[]
  magazines?: tProductMagazineReference[]
  landingPageImageAiGenerated?: LandingPageImageAiGenerated
}

/* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */
const Redux = () => {
  const filter = useSelector(({ Filter }) => ({
    contextKey: Filter.contextKey,
    size: Filter.size,
    page: Filter.page,
    activeFilters: Filter.activeFilters,
    activeBrands: Filter.activeBrands,
    brands: Filter.brands,
  }), shallowEqual)
  const dispatch = useDispatch()

  return { filter, dispatch }
}

/* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */
type NavigationMenuMatch = {
  item: NavigationItem
  parentBrand: NavigationItem | null
}

const findNavigationMenuItem = (
  items: tMenu,
  targetMenuId?: number,
  parentBrand: NavigationItem | null = null,
): NavigationMenuMatch | null => {
  if (!targetMenuId) return null

  for (const item of items) {
    const nextParentBrand = item.brand ? item : parentBrand

    if (item.menueId === targetMenuId) {
      return { item, parentBrand }
    }

    if (item.children?.length) {
      const childMatch = findNavigationMenuItem(item.children, targetMenuId, nextParentBrand)
      if (childMatch) return childMatch
    }
  }

  return null
}

const findNavigationBrandItem = (
  items: tMenu,
  targetBrandId?: number,
  parentBrand: NavigationItem | null = null,
): NavigationMenuMatch | null => {
  if (!targetBrandId) return null

  for (const item of items) {
    const nextParentBrand = item.brand ? item : parentBrand

    if (item.brand === targetBrandId) {
      return { item, parentBrand }
    }

    if (item.children?.length) {
      const childMatch = findNavigationBrandItem(item.children, targetBrandId, nextParentBrand)
      if (childMatch) return childMatch
    }
  }

  return null
}

/* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */
const FilterPage: React.FC<Props> = ({ 
  type, mid, bid,
  title, description,
  products: ssrProducts,
  count: ssrCount,
  productsGeneratedAt,
  initialPage = 1,
  filterMetadata: ssrMetadata,
  metaTitle, metaDescription, metaKeywords,
  canonicalUrl,
  brandLogo,
  stock, sale,
  pageBuilder,
  sitePageBuilder,
  breadcrumb: propBreadcrumb,
  magazines,
  landingPageImageAiGenerated,
}) => {
  const router = useRouter()
  const navigationPaths = useNavigationPaths()
  const { filter, dispatch } = Redux()
  const builderActive = isMenuPageBuilderActive(pageBuilder)
  const builderChromeActive = builderActive || isMenuPageBuilderActive(sitePageBuilder)
  const [requireStock, setRequireStock] = useState(stock === true)
  const navigationBreadcrumb = buildNavigationBreadcrumb(navigationPaths, { menueId: mid, brandId: bid })
  const fallbackBrandBreadcrumb: BreadcrumbItem[] = type === 'brand' && title?.trim()
    ? [
        { title: 'Marken', href: '/brands' },
        { title, href: canonicalUrl ? `/${canonicalUrl}` : undefined },
      ]
    : []
  const fallbackMenuBreadcrumb: BreadcrumbItem[] = (!type || type === 'menu' || type === 'brandMenu') && title?.trim()
    ? [
        { title, href: canonicalUrl ? `/${canonicalUrl}` : undefined },
      ]
    : []
  const breadcrumb = propBreadcrumb?.length
    ? propBreadcrumb
    : navigationBreadcrumb.length
      ? navigationBreadcrumb
      : fallbackBrandBreadcrumb.length
        ? fallbackBrandBreadcrumb
        : fallbackMenuBreadcrumb
  const navigationMenuMatch = findNavigationMenuItem(navigationPaths, mid)
    || (type === 'brand' ? findNavigationBrandItem(navigationPaths, bid) : null)
  const subcategoryItems = sale ? [] : navigationMenuMatch?.item.children || []
  const subcategoryParentBrand = navigationMenuMatch?.parentBrand || null
  const showCatalogFilters = builderChromeActive
    && (!type || type === 'menu' || type === 'brand' || type === 'brandMenu')

  const querySuche = router.query.suche
  const routeSuche = typeof querySuche === 'string'
    ? querySuche
    : Array.isArray(querySuche) ? querySuche[0] || '' : ''
  const contextKey = requestCacheKey(router.pathname, { ...config.shop, type, mid, bid, sale, stock, suche: routeSuche, initialPage })
  const sameContext = filter.contextKey === contextKey
  // Route props are authoritative. A previous route's Redux filters/products
  // must not trigger a broad request or hide this route's server-rendered cards.
  const size = sameContext ? filter.size : 24
  const page = sameContext ? filter.page : initialPage
  const activeFilters = sameContext ? filter.activeFilters : {}
  const activeBrands = sameContext ? filter.activeBrands : []
  const brands = sameContext ? filter.brands : []
  const body = {
    ...config.shop, size, page, suche: routeSuche, fuzzy: false,
    ...(sale ? { sale: true, stock: true } : stock ? { stock: true } : {}),
    options: Object.values(activeFilters), brands: activeBrands, includeMetadata: false,
    ...((type === 'menu' || type === 'brandMenu') ? { menuId: mid } : {}),
    ...((type === 'brand' || type === 'brandMenu') ? { brandId: bid } : {}),
  }
  const requestKey = requestCacheKey('products', body)
  const viewKey = `${sameContext}:${requestKey}`
  const [result, setResult] = useState<{ viewKey: string; data?: filterProductsResponse }>({ viewKey })
  // Keep an already displayed result stable while staying on its view. Once
  // leaving, only the TTL cache may restore it; old component state cannot
  // resurrect an expired page when navigating A -> B -> A.
  if (result.viewKey !== viewKey) setResult({ viewKey })
  const [failure, setFailure] = useState<{ key: string; message: string }>()
  const [, refreshPreview] = useState(0)
  const unfilteredFirstPage = page === 1 && !activeBrands.length && !Object.keys(activeFilters).length
  const canUseServer = page === initialPage && !activeBrands.length && !Object.keys(activeFilters).length && !routeSuche && size === 24
  const canUsePreview = !sale && !type && !mid && !bid && unfilteredFirstPage && routeSuche.length >= 2
  const previewBody = searchPreviewRequest(config.shop, routeSuche)
  const cached = router.isReady ? peekSearchRequest('products', body, isFilterProductsResponse) : undefined
  const complete = cached || (result.viewKey === viewKey ? result.data : undefined)
  const preview = canUsePreview && router.isReady
    ? peekSearchRequest('products', previewBody, isSearchPreviewResponse)
    : undefined
  const error = failure?.key === requestKey ? failure.message : undefined
  const { products, count, loading } = listingPresentation({
    complete: complete?.filterProducts,
    preview: preview?.filterProducts,
    server: ssrProducts ? { products: ssrProducts, count: ssrCount } : undefined,
    canUsePreview,
    canUseServer,
    failed: Boolean(error),
  })
  const searchSuggestions = complete?.searchSuggestions || []
  const searchAdjustment = complete?.searchAdjustment
  const noindex = !type || Boolean(routeSuche) || activeBrands.length > 0
    || Object.keys(activeFilters).length > 0 || size !== 24
  const seoCanonical = canonicalUrl ? listingPagePath(canonicalUrl, page) : '/suche'
  const seoMetaTitle = listingPageTitle(metaTitle || title || (routeSuche ? `Suche nach ${routeSuche}` : 'Suche'), page)

  useEffect(() => {
    // Only a completed, unfiltered global result has a meaningful total count.
    // Header suggestions and partially loaded results are not submitted searches.
    if (!routeSuche) resetCompletedSearch()
    if (!router.isReady || !sameContext || type || mid || bid || !unfilteredFirstPage || !complete || error) return
    const resultCount = complete.filterProducts.count
    if (typeof resultCount === 'number') trackCompletedSearch(routeSuche, resultCount)
  }, [router.isReady, sameContext, type, mid, bid, unfilteredFirstPage, complete, error, routeSuche])

  useEffect(() => {
    setRequireStock(stock === true)
  }, [stock, mid, bid])

  useEffect(() => {
    // Clearing local facets/page-size selections resets Redux to page one.
    // Restore its real URL as well, so reload/back never show another page.
    if (!router.isReady || !sameContext || noindex || !type || !canonicalUrl || page === initialPage) return
    if (typeof router.replace === 'function') {
      void router.replace(`/${listingPagePath(canonicalUrl, page).replace(/^\//, '')}`, undefined, { scroll: false })
    }
  }, [router, sameContext, noindex, type, canonicalUrl, page, initialPage])

  useEffect(() => {
    if (!router.isReady || sameContext) return
    dispatch({ type: 'activateFilterContext', payload: { contextKey, suche: routeSuche, page: initialPage } })
  }, [router.isReady, sameContext, contextKey, routeSuche, initialPage, dispatch])

  useEffect(() => {
    if (!router.isReady || !sameContext) return
    let active = true
    setFailure(undefined)
    dispatch({ type: 'beginFilterRequest', payload: requestKey })
    // ISR already supplied the complete first page. Reuse its fresh snapshot;
    // stale/static-fallback data stays visible while refreshing in the background.
    const serverFresh = canUseServer && ssrProducts && typeof productsGeneratedAt === 'number'
      && Date.now() - productsGeneratedAt >= 0 && Date.now() - productsGeneratedAt < 90_000
    if (serverFresh) dispatch({ type: 'receiveFilterProducts', payload: { requestKey, data: { products: ssrProducts, count: ssrCount || 0 } } })
    const subscription = serverFresh ? undefined : requestSearch('products', body, isFilterProductsResponse)
    subscription?.promise.then(data => {
      if (!active) return
      setResult({ viewKey, data })
      setFailure(undefined)
      dispatch({ type: 'receiveFilterProducts', payload: { requestKey, data: data.filterProducts } })
      if ((type === 'menu' || type === 'brandMenu') && data.menu) setRequireStock(data.menu.stock === true)
    }).catch(() => {
      if (active) setFailure({ key: requestKey, message: 'Die Produkte konnten nicht geladen werden. Bitte versuchen Sie es erneut.' })
    })
    // Facets and brand suggestions may arrive later without delaying the cards.
    if (serverFresh && ssrMetadata) dispatch({ type: 'receiveFilterMetadata', payload: { requestKey, ...ssrMetadata } })
    const metadata = serverFresh && ssrMetadata ? undefined : requestSearch('products', {
      ...body, metadataOnly: true, filterTree: true, filterBrands: true,
    }, isFilterMetadataResponse)
    metadata?.promise.then(data => {
      if (active) dispatch({ type: 'receiveFilterMetadata', payload: { requestKey, ...data.filterMetadata } })
    }).catch(() => { /* Product results remain usable if facets are unavailable. */ })
    const brandRequest = !builderActive && routeSuche.length >= 3
      ? requestSearch('brands', { ...config.shop, size: 3, suche: routeSuche, fuzzy: false }, isBrandsResponse)
      : undefined
    brandRequest?.promise.then(data => {
      if (active) dispatch({ type: 'receiveFilterBrands', payload: { requestKey, brands: data.brands } })
    }).catch(() => { /* Independent suggestion request. */ })
    // Join an already-running header preview, never launch a second preview on
    // direct navigation. The full response always takes precedence.
    const pendingPreview = canUsePreview ? joinSearchRequest('products', previewBody) : undefined
    pendingPreview?.promise.then(() => {
      if (active) refreshPreview(value => value + 1)
    }).catch(() => {})
    return () => {
      active = false
      subscription?.release()
      metadata?.release()
      brandRequest?.release()
      pendingPreview?.release()
    }
    // requestKey contains every request field, including option AND/OR groups.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady, sameContext, requestKey, builderActive, dispatch, productsGeneratedAt])

  // The builder may curate fixed product keys and promotional sections. Only
  // page one is a landing page; deeper URLs must expose their fetched catalog
  // products, regardless of those fixed selections.
  if (builderActive && initialPage === 1 && page === 1) return <LandingPage
    {...{
      pageType: type,
      title,
      description,
      metaTitle: seoMetaTitle,
      metaDescription,
      metaKeywords,
      canonicalUrl: seoCanonical,
      noindex,
      pageBuilder,
      products,
      count,
      page,
      size,
      requireStock,
      breadcrumb,
      subcategoryItems,
      subcategoryParentBrand,
      showCatalogFilters,
      magazines,
      landingPageImageAiGenerated,
      brandLogo,
    }}
  />

  return <Filter {...{
      pageType: type,
      products, page, size, count, loading, error, searchSuggestions, searchAdjustment,
      brands,
      title, description,
      metaTitle: seoMetaTitle, metaDescription, metaKeywords,
      canonicalUrl: seoCanonical,
      noindex,
      brandLogo,
      requireStock,
      breadcrumb,
      subcategoryItems,
      subcategoryParentBrand,
      showCatalogFilters,
      sitePageBuilder,
      magazines,
      landingPageImageAiGenerated,
    }}/>
}

export default FilterPage

/* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */
